import {createRef, useEffect, useRef, useState} from 'react';
import {Stage} from 'react-konva';
import {useDispatch, useSelector, Provider, useStore} from 'react-redux';
import {
  applicationSelector,
  updateCurrentKonvaObject,
  updateDesign
} from '../../slices/application';
import {extractCanvasMeasurementsFromDesign} from '../../utils/utils';
import {useShadowRoot} from '../../contexts/ShadowRootContext';
import './DrawLayer.css';
import TextLayer from './TextLayer';
import rootReducer from '../../slices';
import ImageLayer from './ImageLayer';
import FontFaceObserver from 'fontfaceobserver';

// Auto-scroll the canvas while dragging an object near the viewport edge, so a
// slender/overflowing tube can be designed on without stopping to scroll.
const EDGE_SCROLL_ZONE = 70; // px from each edge that triggers scrolling (deadzone elsewhere)
const EDGE_SCROLL_MAX_SPEED = 16; // px per frame at the very edge

const DrawLayer = () => {
  const store = useStore(rootReducer);
  const dispatch = useDispatch();
  const shadowRoot = useShadowRoot();
  const {design} = useSelector(applicationSelector, (oldState, newState) => {
    return (
      oldState.design.designId === newState.design.designId &&
      oldState.design.printColor === newState.design.printColor &&
      oldState.design.canvasHeight === newState.design.canvasHeight &&
      oldState.design.canvasWidth === newState.design.canvasWidth &&
      oldState.design.objects == newState.design.objects
    );
  });
  const {currentKonvaObject} = useSelector(applicationSelector);
  const [measurements, setMeasurements] = useState(extractCanvasMeasurementsFromDesign(design));
  const stageRef = createRef(null);
  const textLayerRef = createRef(null);
  const imageLayerRef = createRef(null);
  const [fontsLoaded, setFontsLoaded] = useState(false);
  const autoScrollRef = useRef({raf: 0, dir: 0, speed: 0, node: null});

  useEffect(() => {
    const updateDesignSize = () => {
      const drawingElement = shadowRoot.getElementsByClassName?.('drawing')?.[0] || shadowRoot.querySelector?.('.drawing');
      // Width comes from the wrapper (the stable viewport); the drawing layer
      // itself grows wider than the viewport when the tube overflows for scroll.
      const wrapperElement =
        shadowRoot.getElementsByClassName?.('hs-canvas-wrapper')?.[0] ||
        shadowRoot.querySelector?.('.hs-canvas-wrapper');
      if (drawingElement) {
        const clientHeight = drawingElement.clientHeight;
        const clientWidth = wrapperElement?.clientWidth ?? drawingElement.clientWidth;
        dispatch(updateDesign({canvasWidth: clientWidth, canvasHeight: clientHeight}));
      }
    };

    // Call once to set initial size
    updateDesignSize();

    // Set up event listener to update size when window is resized
    window.addEventListener('resize', updateDesignSize);

    // Clean up event listener when component is unmounted
    return () => {
      window.removeEventListener('resize', updateDesignSize);
    };
  }, [dispatch]);

  useEffect(() => {
    const fontNames = [
      'Roboto Slab',
      'Montserrat',
      'Bebas Neue',
      'Comfortaa',
      'Alfa Slab One',
      'Pacifico',
      'Roboto Mono',
      'Courier Prime',
      'Yellowtail'
    ];
    const fontObservers = fontNames.map((fontName) => new FontFaceObserver(fontName));

    Promise.all(fontObservers.map((observer) => observer.load()))
      .then(() => {
        setFontsLoaded(true);
      })
      .catch((error) => {
        console.error('Font loading error:', error);
      });
  }, []);

  useEffect(() => {
    setMeasurements(extractCanvasMeasurementsFromDesign(design));
  }, [design]);

  function moveSelectedX(value) {
    const deltaX = measurements.canvasWidth / 100 / measurements.tubeWidth;
    dispatch(updateCurrentKonvaObject({x: currentKonvaObject.x + deltaX * value}));
  }

  function moveSelectedY(value) {
    const deltaY = measurements.canvasHeight / 100 / measurements.tubeHeight;
    dispatch(updateCurrentKonvaObject({y: currentKonvaObject.y + deltaY * value}));
  }

  const handleKeyDown = (e) => {
    if (currentKonvaObject) {
      switch (e.key) {
        case 'Delete':
        case 'Backspace':
          if (document.activeElement.type != 'text') {
            const index = design.objects.findIndex((obj) => obj.id == currentKonvaObject.id);
            if (index > -1) {
              const objects = [...design.objects];
              objects.splice(index, 1);
              dispatch(updateDesign({objects: objects}));
            }
            dispatch(updateCurrentKonvaObject(null));
          }
          break;
        case 'ArrowLeft':
          e.preventDefault();
          moveSelectedX(-1);
          break;
        case 'ArrowRight':
          e.preventDefault();
          moveSelectedX(1);
          break;
        case 'ArrowDown':
          e.preventDefault();
          moveSelectedY(1);
          break;
        case 'ArrowUp':
          e.preventDefault();
          moveSelectedY(-1);
          break;
        default:
      }
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentKonvaObject]);

  const checkDeselect = (e) => {
    const clickedOnNew = e.target.attrs?.id != currentKonvaObject?.id;
    if (clickedOnNew) {
      saveSelected();
      // deselect when clicked on empty area
      if (e.target == e.target.getStage()) dispatch(updateCurrentKonvaObject(null));
    }
  };

  function saveSelected() {
    let canvasObject = textLayerRef.current.findOne('#' + currentKonvaObject?.id);
    if (canvasObject == null)
      canvasObject = imageLayerRef.current.findOne('#' + currentKonvaObject?.id);

    if (canvasObject == null) return;

    const index = design.objects.findIndex((object) => object.id === currentKonvaObject?.id);
    const objects = [...design.objects];
    if (index === -1) {
      objects.push(currentKonvaObject);
      dispatch(updateDesign({objects: objects}));
    } else if (JSON.stringify(design.objects[index]) !== JSON.stringify(currentKonvaObject)) {
      objects[index] = currentKonvaObject;
      dispatch(updateDesign({objects: objects}));
    }
  }

  const getScrollContainer = () =>
    shadowRoot.getElementsByClassName?.('hs-canvas')?.[0] || shadowRoot.querySelector?.('.hs-canvas');

  const stopAutoScroll = () => {
    const state = autoScrollRef.current;
    if (state.raf) cancelAnimationFrame(state.raf);
    state.raf = 0;
    state.dir = 0;
    state.node = null;
  };

  const stepAutoScroll = () => {
    const state = autoScrollRef.current;
    const container = getScrollContainer();
    if (!container || state.dir === 0 || !state.node) {
      state.raf = 0;
      return;
    }
    const maxScroll = container.scrollWidth - container.clientWidth;
    const next = Math.min(maxScroll, Math.max(0, container.scrollLeft + state.dir * state.speed));
    const applied = next - container.scrollLeft;
    if (applied !== 0) {
      container.scrollLeft = next;
      // Keep the dragged object under the pointer as the canvas scrolls beneath it.
      state.node.x(state.node.x() + applied);
      state.node.getLayer()?.batchDraw();
    }
    state.raf = requestAnimationFrame(stepAutoScroll);
  };

  const handleStageDragMove = (e) => {
    const container = getScrollContainer();
    // Only when the canvas actually scrolls horizontally (slender-tube overflow).
    if (!container || container.scrollWidth <= container.clientWidth) {
      stopAutoScroll();
      return;
    }
    const nativeEvent = e.evt;
    const clientX = nativeEvent?.touches ? nativeEvent.touches[0]?.clientX : nativeEvent?.clientX;
    if (clientX == null) return;

    const rect = container.getBoundingClientRect();
    const x = clientX - rect.left;
    const state = autoScrollRef.current;

    // Deadzone everywhere except within EDGE_SCROLL_ZONE of the left/right edges.
    let dir = 0;
    let depth = 0;
    if (x < EDGE_SCROLL_ZONE) {
      dir = -1;
      depth = EDGE_SCROLL_ZONE - x;
    } else if (x > rect.width - EDGE_SCROLL_ZONE) {
      dir = 1;
      depth = x - (rect.width - EDGE_SCROLL_ZONE);
    }

    if (dir === 0) {
      stopAutoScroll();
      return;
    }
    state.dir = dir;
    state.speed = EDGE_SCROLL_MAX_SPEED * Math.min(1, depth / EDGE_SCROLL_ZONE);
    state.node = e.target;
    if (!state.raf) state.raf = requestAnimationFrame(stepAutoScroll);
  };

  // Stop any running auto-scroll if the layer unmounts mid-drag.
  useEffect(() => stopAutoScroll, []);

  return (
    <div className="drawing" id="background" style={{width: measurements.canvasWidth}}>
      {fontsLoaded ? (
        <Stage
          width={measurements.canvasWidth}
          height={measurements.canvasHeight}
          onMouseDown={checkDeselect}
          onTouchStart={checkDeselect}
          onDragMove={handleStageDragMove}
          onDragEnd={stopAutoScroll}
          ref={stageRef}>
          <Provider store={store}>
            <TextLayer layerRef={textLayerRef} measurements={measurements} />
            <ImageLayer layerRef={imageLayerRef} measurements={measurements} />
          </Provider>
        </Stage>
      ) : (
        <div>Loading Fonts...</div>
      )}
    </div>
  );
};

export default DrawLayer;
